import {SummaryContext} from '../summaryContext';
import { supabase } from '../supabaseClient';
import {ProposalContext, ProposalDraft, validProposalStyleWarnings} from '../proposalContext';
import {ProductFieldWarnings} from '../productQualifications';

export const rewriteGoals=['Shorter','Clearer','More conversational','More like QuikPix','Less corporate','More direct','Warmer','Custom instruction'] as const;
export type RewriteGoal=typeof rewriteGoals[number];
export interface RewriteRequest {original:string;context:string;goals:RewriteGoal[];custom:string;protected_names:string[]}
export interface RewriteDraft {revised_text:string;warnings:string[]}
export async function generateRewrite(input:RewriteRequest):Promise<RewriteDraft> {
  const {draft}=await generate({action:'rewrite',...input});
  if(!draft||typeof draft.revised_text!=='string'||!Array.isArray(draft.warnings)||!draft.warnings.every((w:unknown)=>typeof w==='string'))throw new Error('Rewrite unavailable.');
  return draft;
}

async function generate(payload: object,route='/api/text-generation') {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) throw new Error('AI generation unavailable. Please sign in and try again.');
  try {
    const response = await fetch(route, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` },
      body: JSON.stringify(payload), signal: AbortSignal.timeout(35000),
    });
    if (!response.ok) throw new Error('unavailable');
    return await response.json();
  } catch { throw new Error('AI generation unavailable. Please try again later. Your existing content has not changed.'); }
}
export async function generateProductDraft(context: object): Promise<WriterDraft> {
  const result = await generate({ action: 'product-writer', context });
  const d=result.draft;
  const warningFields=['short_description','proposal_description','key_features'];
  if (!d || !['short_description','proposal_description','equipment_summary','production_notes'].every(k=>typeof d[k]==='string') ||
    !['key_features','clarification_questions','warnings'].every(k=>Array.isArray(d[k])&&d[k].every((v:unknown)=>typeof v==='string')) ||
    !d.field_warnings || Object.keys(d.field_warnings).length!==warningFields.length ||
    !warningFields.every(k=>Array.isArray(d.field_warnings[k])&&d.field_warnings[k].length<=1&&d.field_warnings[k].every((v:unknown)=>v==='qualification_preservation_failed'))) throw new Error('AI generation unavailable.');
  return result.draft;
}
export interface WriterDraft {
  short_description:string; proposal_description:string; key_features:string[];
  equipment_summary:string; production_notes:string; clarification_questions:string[]; warnings:string[];
  field_warnings:ProductFieldWarnings;
}
export async function generateProposalDraft(context:ProposalContext,phase:'assess'|'generate'='generate'):Promise<ProposalDraft> {
  const result=await generate({action:'proposal-writer',phase,context});
  const d=result.draft;
  if(!d||!['executive_vision','short_opening','experience_rationale'].every(k=>typeof d[k]==='string')||
    !['clarification_questions','warnings'].every(k=>Array.isArray(d[k])&&d[k].every((v:unknown)=>typeof v==='string'))||
    !Array.isArray(d.product_proposal_copy)||d.product_proposal_copy.some((p:any)=>!p||!Number.isInteger(p.product_index)||!context.products[p.product_index]||typeof p.copy!=='string')||
    !validProposalStyleWarnings(d.field_warnings,d.product_proposal_copy.length))throw new Error('AI generation unavailable.');
  return d;
}

export async function generateConversationalSummary(context:SummaryContext,request:import('../conversationState').ConversationRequest):Promise<import('../conversationState').ConversationResult>{
 const {draft}=await generate({action:'conversational-summary',context,...request},'/api/executive-summary');
 if(!draft||typeof draft.summary!=='string'||draft.summary.length>2400||!Array.isArray(draft.questions)||draft.questions.some((q:unknown)=>typeof q!=='string')||(draft.questions.length&&draft.summary)||!Array.isArray(draft.warnings)||draft.warnings.some((w:unknown)=>typeof w!=='string'))throw Error('Summary unavailable.');return draft;
}
